/**
 * scoreEventForPlacement — the ONE authoritative event data-quality scorer
 * (COR-18, project decision D6: it lives in `@loci/validation`, NOT a new
 * package).
 *
 * This consolidates the dimension logic of the legacy
 * `calculateDataQualityScore`
 * (`services/data-pipeline/src/services/data-quality.service.ts`) — its
 * source-trust reliability map, its completeness / location / temporal /
 * source / readiness intuitions — onto a single 0–100 scale with the LOCKED
 * COR-18 weights, machine-readable {@link QualityReasonCode}s, hard-fail
 * quarantine, and placement caps.
 *
 * COR-24 extends the completeness dimension to recognise the legacy synthetic
 * Ticketmaster `buildEventDescription` boilerplate and withhold description
 * credit for it (see {@link SYNTHETIC_DESCRIPTION_SKELETON}). Missing image /
 * ticket-URL completeness penalties already live in this dimension.
 *
 * COR-25 adds the `LOCATION_NAME_IS_ADDRESS` hard fail: a non-empty venue
 * "name" that is actually a raw street address (e.g. `2710 Tuckaseegee Rd`)
 * quarantines just like a missing one (see {@link isAddressLikeVenueName}).
 *
 * PIP-167 (decision D6) adds the `PRICE_STORY_MISSING` hard fail: an event with
 * no price evidence at all (see {@link hasPriceStory}) quarantines for admin
 * review rather than surfacing behind a CTA that cannot tell the truth. It is
 * the one OPT-IN rule here — it fires only when the caller passes
 * `priceStoryRuleEnabled` (resolved from `LOCI_PRICE_STORY_RULE`, default OFF),
 * because it is only honest once PIP-161's extraction pass has populated the
 * price columns. Passing the flag rather than reading the env keeps the purity
 * contract below intact.
 *
 * COR-115 adds the `SOURCE_LINK_DEAD` hard fail, driven by the persisted
 * `Event.linkStatus` verdict ({@link ScorableEvent.linkStatus}). PIP-163's
 * liveness sweeper and PIP-255's Eventbrite dead-listing path both quarantine a
 * row whose link is dead, but they write `qualityStatus` directly — so before
 * this rule the next re-score (an ingest re-see, a backfill pass, a venue edit)
 * rewrote the band from structural fields alone and silently lifted the
 * quarantine. The verdict is an INPUT, not a fetch: the scorer stays pure.
 *
 * ## Purity (HARD requirement)
 *
 * The scorer is **pure and deterministic**: same input → same output, with NO
 * `Date.now()`, no clock, no randomness. It evaluates temporal *integrity*
 * (end-after-start coherence, plausible duration, timezone presence) but NEVER
 * freshness or "already ended" — those are clock-relative and belong to a
 * separate, caller-supplied freshness pass.
 *
 * ## Subscores vs. weights
 *
 * Each dimension produces a 0–100 subscore (its own attainment). The final
 * `score` is the weighted sum, rounded to an integer 0–100:
 *
 *   completeness·0.30 + location·0.25 + temporal·0.20 + source·0.15 + readiness·0.10
 *
 * ## Bands (applied to the structural score, then caps, then quarantine wins)
 *
 *   PREMIUM         90–100  (and no critical placement defect)
 *   STANDARD        75–89
 *   LOW_CONFIDENCE  60–74
 *   QUARANTINED     <60, or any hard-fail
 */

import { DEAD_LINK_STATUS, isSeriesEvergreenSource } from '@loci/types';
import type {
  QualityReasonCode,
  QualityStatus,
  QualitySubscores,
  ScorableEvent,
} from '@loci/types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * LOCKED dimension weights — must sum to 1.0.
 *
 * Exported (module-level only, like {@link isAddressLikeVenueName}; not
 * re-exported from the package index) so tests can derive expected weighted
 * penalties from the real constants instead of hand-copied magic numbers
 * (PIP-135).
 */
export const WEIGHTS = {
  completeness: 0.3,
  location: 0.25,
  temporal: 0.2,
  source: 0.15,
  readiness: 0.1,
} as const;

/** Band thresholds on the structural (pre-cap) score. */
const BAND_PREMIUM_MIN = 90;
const BAND_STANDARD_MIN = 75;
const BAND_LOW_CONFIDENCE_MIN = 60;

/** Placement caps expressed as a maximum attainable score. */
const CAP_STANDARD_MAX = 89;
const CAP_LOW_CONFIDENCE_MAX = 74;

/** Hard-fail score floor. */
const QUARANTINE_SCORE = 0;

/** Completeness signals. */
const MIN_DESCRIPTION_CHARS = 80;
/** A title shorter than this (after trim) is treated as too thin to be useful. */
const MIN_USEFUL_TITLE_CHARS = 3;

/**
 * COR-24 — the legacy Ticketmaster `buildEventDescription` synthesizer
 * (removed on the ingest path by PIP-141) fabricated descriptions of the shape
 *
 *   "{genre} event. at {venue}. presented by {promoter}. Tickets on sale from {date}."
 *
 * and two degenerate variants: "Undefined event." (missing genre) and the
 * "presented by PROMOTED BY VENUE" placeholder-promoter form. These strings are
 * long enough to clear {@link MIN_DESCRIPTION_CHARS} yet carry zero real editorial
 * value, so without detection a ~90%-synthetic TM row could still earn full
 * description credit and surface as confident. EXISTING rows still carry this
 * pattern until the PIP-143 backfill runs, so the scorer must recognise and
 * withhold credit for it (treated identically to a null/empty/too-short
 * description — same `IDENTITY_DESCRIPTION_SHORT` reason, no parallel code).
 *
 * Matched case-insensitively against the trimmed description:
 *   - the full template skeleton ("… event. at … presented by … Tickets on sale from …"),
 *   - the bare "Undefined event." garbage form,
 *   - any description containing the "presented by PROMOTED BY VENUE" placeholder.
 *
 * The skeleton regex is anchored on the template's literal connective tokens
 * (`event. at … presented by … tickets on sale from`) so real source copy that
 * merely mentions "tickets on sale" or "presented by" in prose does not match.
 */
const SYNTHETIC_DESCRIPTION_SKELETON =
  /\bevent\.\s+at\b.*\bpresented by\b.*\btickets on sale from\b/i;
const SYNTHETIC_DESCRIPTION_UNDEFINED = /^undefined event\.?$/i;
const SYNTHETIC_DESCRIPTION_PLACEHOLDER_PROMOTER = /presented by promoted by venue/i;

/**
 * PIP-407 — product nouns that, as the LAST word(s) of a title, mark the
 * listing as a standing commerce product rather than a dated event
 * ("Foodies + New Friends | monthly membership", "DIY workshop reservation",
 * "Studio gift card"). Matched word-wise against the title's final words after
 * {@link trailingTitleWords}; see {@link standingProductTitleNoun} for the
 * false-positive guards.
 *
 * A closed list on purpose (the `TITLE_STOP_PHRASES` discipline in
 * data-pipeline's `canonicalEventKey.ts`): every addition needs a test
 * proving a real event title does not trip it.
 */
const STANDING_PRODUCT_NOUNS: ReadonlyMap<string, string> = new Map([
  ['membership', 'membership'],
  ['memberships', 'membership'],
  ['subscription', 'subscription'],
  ['subscriptions', 'subscription'],
  ['reservation', 'reservation'],
  ['reservations', 'reservation'],
]);

/** Final words that are a gift product only after `gift` (`gift card`, `e-gift voucher`). */
const GIFT_PRODUCT_NOUNS: ReadonlySet<string> = new Set([
  'card',
  'cards',
  'certificate',
  'certificates',
  'voucher',
  'vouchers',
]);

/**
 * A product noun preceded by one of these is a NAME, not a product:
 * "Live at the Reservation", "Meet the Membership".
 */
const PRODUCT_NOUN_ARTICLES: ReadonlySet<string> = new Set(['the', 'a', 'an']);

/** Earth coordinate bounds. */
const LAT_MIN = -90;
const LAT_MAX = 90;
const LNG_MIN = -180;
const LNG_MAX = 180;

/**
 * Service-area plausibility bounds (continental US + a generous margin). The
 * CLT MVP only ingests US events; a coordinate far outside these bounds is
 * almost certainly a geocode error (swapped axes, default 0,0, etc.) even
 * though it is technically on Earth.
 */
const SERVICE_AREA = {
  latMin: 18, // south of Hawaii / PR
  latMax: 72, // northern Alaska
  lngMin: -180,
  lngMax: -64, // east of Maine / PR
} as const;

/** Temporal integrity: plausible event duration window. */
const MIN_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const MAX_DURATION_MS = 72 * 60 * 60 * 1000; // 72 hours

/**
 * Categories whose multi-day runs are legitimately long (festivals, expos,
 * conferences, exhibitions). For these the 72h upper bound is relaxed.
 */
const LONG_DURATION_CATEGORIES = new Set<string>([
  'festival',
  'festivals',
  'expo',
  'conference',
  'conferences',
  'exhibition',
  'exhibitions',
  'convention',
]);
/** A generous relaxed cap for the long-duration category exception (~2 weeks). */
const MAX_DURATION_MS_LONG = 14 * 24 * 60 * 60 * 1000;

/**
 * Placeholder titles that read as "no real title". Compared case-insensitively
 * against the trimmed title.
 */
const PLACEHOLDER_TITLES = new Set<string>([
  'untitled event',
  'untitled',
  'tbd',
  'tba',
  'coming soon',
  'event',
  'no title',
  'n/a',
]);

/**
 * COR-25 — street-type tokens that mark a venue "name" as an address fragment
 * rather than a real venue name. Matched as whole, lower-cased tokens (the
 * name is split on non-alphanumeric boundaries, so `Rd.`, `rd,` and `Rd`
 * all normalize to `rd`). Covers the common USPS abbreviations AND their
 * long forms, plus unit designators (`ste`, `suite`, `unit`).
 *
 * Deliberately EXCLUDES bare directional/ordinal words (`east`, `54`) and
 * cuisine/venue nouns, so number-named venues like `300 East`, `Studio 54`,
 * or `1900 Mexican Grill` never match.
 *
 * COR-86 — the original list stopped at the 13 most common suffixes, which
 * made part 1 return `false` for 5 of the 138 address-shaped CLT venue names
 * measured on the Neon `production` branch 2026-09-09: `604 Doug Mayes Pl`,
 * `14045 Ballantyne Corporate Pl`, `905 Pearl Park Wy`, `4420 Hoyt Galvin
 * Way`, `2815 The Plaza`. The `pl/place`, `way/wy`, `plaza`, `sq/square`,
 * `trl/trail`, `loop`, `pt/point` and `xing` family is added here rather than
 * at the call sites so both halves of the rule (this set and db-core's
 * `unresolvedVenueExclusionSql`, which builds its POSIX alternation from it)
 * widen together.
 *
 * Widening this set is safe against real venues for two structural reasons:
 * part 1 also requires a LEADING house number (`Prosperity Place`, `The
 * Plaza Midwood` never reach the token check), and part 2 requires the
 * `address` column to corroborate. Both remain in force.
 *
 * API-1700 — EXPORTED so db-core's `unresolvedVenueExclusionSql` can build the
 * raw-SQL half of this same signature from the one list, rather than
 * hand-copying 26 tokens into a Postgres regex that would then drift. Same
 * one-module-owns-both-halves discipline as COR-66's `venueListCategorySql`.
 */
export const STREET_TYPE_TOKENS: ReadonlySet<string> = new Set<string>([
  'rd',
  'road',
  'st',
  'street',
  'ave',
  'avenue',
  'dr',
  'drive',
  'ln',
  'lane',
  'blvd',
  'boulevard',
  'pkwy',
  'parkway',
  'hwy',
  'highway',
  'ter',
  'terrace',
  'cir',
  'circle',
  'ct',
  'court',
  'ste',
  'suite',
  'unit',
  // COR-86 — same USPS family, missing from the original list.
  'pl',
  'place',
  'way',
  'wy',
  'plaza',
  'sq',
  'square',
  'trl',
  'trail',
  'loop',
  'pt',
  'point',
  'xing',
]);

/**
 * Leading house/street number: 1–6 digits, optional single letter (e.g. `123A`).
 *
 * API-1708 — EXPORTED so db-core's `unresolvedVenueExclusionSql`, which has to
 * spell this in Postgres ARE syntax (no `\b`, no locale-dependent `\s`), can pin
 * its copy to this regex in a test. Changing the pattern fails that pin until
 * the SQL half is updated to match.
 */
export const LEADING_HOUSE_NUMBER = /^(\d{1,6})[a-z]?\b/i;

/**
 * COR-86 — an ordinal street NAME (`4th`, `32nd`, `15th`), fully anchored so a
 * bare house number, unit number or zip can never match. Used to rescue these
 * tokens from {@link isAddressLikeVenueName}'s street-name overlap check,
 * which previously discarded every digit-leading token.
 *
 * Anchored, single bounded quantifier, one alternation of literals: linear,
 * no backtracking risk.
 */
const ORDINAL_STREET_NAME = /^\d{1,3}(?:st|nd|rd|th)$/;

/**
 * Source provenance trust tier, ported from the legacy
 * `sourceReliability` map (`data-quality.service.ts` ~line 147), keyed by a
 * lower-cased source label. Values are 0–1 (multiplied to 0–100 by the
 * dimension). A source below {@link SOURCE_LOW_TRUST_THRESHOLD} emits
 * `SOURCE_LOW_TRUST` (a placement CAP).
 */
const SOURCE_TRUST: Record<string, number> = {
  official: 1.0,
  ticketmaster: 0.9,
  eventbrite: 0.8,
  facebook: 0.7,
  manual: 0.6,
  api: 0.5,
  scraped: 0.4,
  seed: 0.4,
  unknown: 0.2,
};
const SOURCE_TRUST_UNKNOWN = 0.2;
/** Provenance trust below this tier is a low-trust source (CAP to STANDARD). */
const SOURCE_LOW_TRUST_THRESHOLD = 0.5;

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

/**
 * Optional caller-supplied context for signals NOT derivable from a
 * `ScorableEvent` alone (they depend on external review/dedup state). Keeping
 * them as inputs preserves the scorer's purity.
 */
export interface ScorePlacementContext {
  /** A human-review flag is open and unresolved → cap at LOW_CONFIDENCE. */
  readonly reviewFlagUnresolved?: boolean;
  /** A fuzzy-duplicate is pending merge → cap at LOW_CONFIDENCE. */
  readonly fuzzyDuplicatePendingMerge?: boolean;
  /** A safety/compliance flag is set → immediate hard-fail quarantine. */
  readonly safetyFlag?: boolean;
  /**
   * PIP-167 (decision D6) — activate the `PRICE_STORY_MISSING` hard fail.
   *
   * This is a CONTEXT input rather than an env read because the scorer is pure
   * by contract (see the module doc). Callers resolve it from
   * `LOCI_PRICE_STORY_RULE` via `getPriceStoryRulePolicy()` (`@loci/config`) and
   * pass the resolved boolean here.
   *
   * Defaults to `false` (rule OFF) when omitted: scoring is then byte-identical
   * to pre-PIP-167 behaviour, which is what makes the rollout safe. A call site
   * that forgets to pass it under-enforces rather than mass-quarantining.
   */
  readonly priceStoryRuleEnabled?: boolean;
}

/** The authoritative scorer output. */
export interface PlacementScore {
  /** Final weighted score, integer 0–100 (0 on hard-fail). */
  readonly score: number;
  /** Placement band. */
  readonly status: QualityStatus;
  /** Every hard-fail, deduction, and cap that fired, in evaluation order. */
  readonly reasons: QualityReasonCode[];
  /** Per-dimension 0–100 attainment (persisted to the Json column). */
  readonly subscores: QualitySubscores;
}

// ---------------------------------------------------------------------------
// Small pure helpers
// ---------------------------------------------------------------------------

function trimmedOrEmpty(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

function isPlaceholderTitle(title: string): boolean {
  return PLACEHOLDER_TITLES.has(title.toLowerCase());
}

/** Trailing aside closers this rule drops, mapped to their opener. */
const ASIDE_OPENER_FOR_CLOSER: Readonly<Record<string, string>> = { ')': '(', ']': '[' };

/** Whitespace test for ONE character (constant time; never run over a string). */
const SINGLE_WHITESPACE = /^\s$/;

/** An ASCII word character after lower-casing — every product noun is ASCII. */
function isTitleWordChar(char: string): boolean {
  return (char >= 'a' && char <= 'z') || (char >= '0' && char <= '9');
}

/**
 * The index just past the title once trailing whitespace and SIMPLE trailing
 * asides — `(…)` with no parenthesis inside, `[…]` with no bracket inside —
 * are dropped: `"Rug Making | Reservation (2 hrs) [online] "` ends after
 * `Reservation`.
 *
 * A hand-written backward scan, not a regex: titles are uncontrolled upstream
 * text, and the regex form (`(?:\s*(?:\([^()]*\)|…))+\s*$`) backtracks
 * polynomially on long runs of spaces, `)(` or `][`. Each character is
 * visited a bounded number of times, so this is O(n) on any input.
 */
function endBeforeTrailingAsides(title: string): number {
  let end = title.length;
  for (;;) {
    while (end > 0 && SINGLE_WHITESPACE.test(title.charAt(end - 1))) end -= 1;
    const closer = title.charAt(end - 1);
    const opener = ASIDE_OPENER_FOR_CLOSER[closer];
    if (end === 0 || opener === undefined) return end;
    let i = end - 2;
    while (i >= 0 && title.charAt(i) !== opener && title.charAt(i) !== closer) i -= 1;
    // No opener, or a nested/unbalanced closer first: not a simple aside, keep it.
    if (i < 0 || title.charAt(i) === closer) return end;
    end = i;
  }
}

/**
 * The title's last two lower-cased ASCII words, read backward from the end of
 * the title proper (after {@link endBeforeTrailingAsides}); anything that is
 * not `[a-z0-9]` separates words, so `"e-Gift"` reads as `e`, `gift`. Only
 * the trailing words are scanned. Linear.
 */
function trailingTitleWords(title: string): {
  readonly last: string | undefined;
  readonly previous: string | undefined;
} {
  let i = endBeforeTrailingAsides(title) - 1;
  const readWordBackward = (): string | undefined => {
    while (i >= 0 && !isTitleWordChar(title.charAt(i).toLowerCase())) i -= 1;
    if (i < 0) return undefined;
    const wordEnd = i + 1;
    while (i >= 0 && isTitleWordChar(title.charAt(i).toLowerCase())) i -= 1;
    return title.slice(i + 1, wordEnd).toLowerCase();
  };
  const last = readWordBackward();
  const previous = last === undefined ? undefined : readWordBackward();
  return { last, previous };
}

/**
 * PIP-407 — the standing-product noun a title ENDS with, or `null`.
 *
 * Anchored at the end of the title (after trailing parentheticals), because
 * that is where a product listing names what it sells: "… | monthly
 * membership", "DIY workshop reservation". The anchoring is the
 * false-positive guard — a show merely named after the word keeps the word
 * up front or in the middle and never matches:
 *
 *   - "Reservation Dogs Live"                 → null (ends "live")
 *   - "Annual Membership Meeting"             → null (ends "meeting")
 *   - "Wine Tasting - Reservations Required"  → null (ends "required")
 *   - "Live at the Reservation"               → null (article before the noun)
 *
 * Exported (module-level and from the package index) so the reason is
 * explainable in tests and admin tooling without re-implementing the rule.
 */
export function standingProductTitleNoun(title: string): string | null {
  const { last, previous } = trailingTitleWords(title);
  if (last === undefined) return null;
  if (previous !== undefined && PRODUCT_NOUN_ARTICLES.has(previous)) return null;
  const noun = STANDING_PRODUCT_NOUNS.get(last);
  if (noun !== undefined) return noun;
  if (GIFT_PRODUCT_NOUNS.has(last) && (previous === 'gift' || previous === 'egift')) {
    return 'gift card';
  }
  return null;
}

/**
 * COR-24 — true when a (already-trimmed, non-empty) description is the legacy
 * synthetic Ticketmaster `buildEventDescription` boilerplate rather than real
 * editorial copy. See {@link SYNTHETIC_DESCRIPTION_SKELETON} for the rationale.
 */
function isSyntheticDescription(description: string): boolean {
  return (
    SYNTHETIC_DESCRIPTION_SKELETON.test(description) ||
    SYNTHETIC_DESCRIPTION_UNDEFINED.test(description) ||
    SYNTHETIC_DESCRIPTION_PLACEHOLDER_PROMOTER.test(description)
  );
}

/**
 * PIP-232 — identity copy the completeness dimension scores.
 *
 * Prefer a genuine, long, non-synthetic `description`. Otherwise concatenate
 * `importantInfo` + `additionalInfo` (TM policy columns) so a row whose
 * description was nulled after the info/pleaseNote split keeps the same
 * credit it earned when that policy lived in `description`. Falls back to
 * the (short/synthetic) description so the existing reason-code path still
 * fires when neither field is useful.
 */
function resolveIdentityCopy(event: ScorableEvent): string {
  const description = trimmedOrEmpty(event.description);
  if (description.length >= MIN_DESCRIPTION_CHARS && !isSyntheticDescription(description)) {
    return description;
  }
  const important = trimmedOrEmpty(event.importantInfo);
  const additional = trimmedOrEmpty(event.additionalInfo);
  const policy =
    important.length > 0 && additional.length > 0
      ? `${important} ${additional}`
      : important.length > 0
        ? important
        : additional;
  return policy.length > 0 ? policy : description;
}

/**
 * COR-86 — USPS directional abbreviations and the words they stand for.
 *
 * A CLT venue name spells the direction short (`150 W 32nd St`) while the
 * `address` column spells it out (`150 West 32nd Street, …`). Without this map
 * the two strings never relate: `w` is not a prefix of `west`, and `w` never
 * equals `west` in the shared-token check.
 *
 * On the 12 cause-B rows measured 2026-09-09 this expansion and the ordinal
 * fix below are each independently sufficient — the expansion restores the
 * prefix relation, the ordinal fix restores the token overlap, and either
 * alone flags all 12. The expansion earns its place on the shapes the ordinal
 * fix cannot reach: a name whose street identity is a direction and whose
 * suffix is abbreviated (`1600 E Blvd` vs `1600 East Blvd, Charlotte`) has no
 * ordinal to match on, and `blvd` is not a prefix of `boulevard`.
 *
 * Expansion is applied symmetrically to BOTH sides of every comparison. The
 * only prefix relation it can break is one that rested on a PARTIAL-token
 * match — `123 Main St S` reading as a prefix of `123 Main St Something` —
 * which was never real agreement in the first place.
 */
const DIRECTION_EXPANSIONS: ReadonlyMap<string, string> = new Map<string, string>([
  ['n', 'north'],
  ['s', 'south'],
  ['e', 'east'],
  ['w', 'west'],
  ['ne', 'northeast'],
  ['nw', 'northwest'],
  ['se', 'southeast'],
  ['sw', 'southwest'],
]);

/**
 * COR-86 — the expanded direction words, as a set.
 *
 * A direction is a MODIFIER on a street name, not a street name — `150 W Main
 * St` vs `150 W Oak St` share `west` and nothing else, and letting that count
 * as agreement flags a venue whose address column actively disagrees. So the
 * overlap check DEMOTES these tokens.
 *
 * **Demotes, never excludes — and the difference is load-bearing.** On some
 * streets the direction IS the street name: `1600 East Blvd`, `4400 South
 * Blvd`, `1200 West Blvd` are real, venue-dense Charlotte streets. Those names
 * carry no other identity token, so excluding directions outright empties the
 * name's token set and returns `false` — and because `blvd` is not a string
 * prefix of `boulevard` (unlike `rd`/`road` or `st`/`street`), the prefix
 * branch does not catch them either. All three are flagged today; excluding
 * would silently un-quarantine their events and drop their admin rename
 * prompt. So a direction is dropped only when the name has some OTHER identity
 * token to match on, which is exactly the case the demotion exists for.
 */
const DIRECTION_WORDS: ReadonlySet<string> = new Set<string>(DIRECTION_EXPANSIONS.values());

/**
 * COR-25 — collapse a name/address for structural comparison: lower-case,
 * strip punctuation (`,` `.` `#`), collapse runs of whitespace. COR-86 adds a
 * whole-token expansion of the directional abbreviations in
 * {@link DIRECTION_EXPANSIONS} (`w` → `west`), which is why the split/join
 * replaces the previous single `.replace` chain.
 *
 * Pure string work, no regex backtracking risk (single linear character-class
 * passes; the map lookup is O(1) per token).
 */
function normalizeForAddressComparison(value: string): string {
  const collapsed = value.toLowerCase().replace(/[.,#]/g, ' ').replace(/\s+/g, ' ').trim();
  if (collapsed.length === 0) return collapsed;
  return collapsed
    .split(' ')
    .map((token) => DIRECTION_EXPANSIONS.get(token) ?? token)
    .join(' ');
}

/**
 * COR-25 — true when a venue "name" is actually a raw street address
 * (e.g. `2710 Tuckaseegee Rd`, `123 Main St`,
 * `2435 Cumberland Ave Charlotte, NC 28203`) rather than a real venue name.
 *
 * Detection is a deliberately PRECISE two-part signature:
 *
 * 1. **Name shape** — the trimmed name starts with a house/street number
 *    ({@link LEADING_HOUSE_NUMBER}) AND contains a street-type token
 *    ({@link STREET_TYPE_TOKENS}, whole-token match) or a `#`-prefixed unit
 *    marker (`#300`).
 * 2. **Address corroboration** — when the `address` column has content, the
 *    name must agree with it: either the normalized name is a prefix of the
 *    normalized address (or vice versa — the name is often the FULLER string,
 *    embedding city/state/zip), or both share the same leading house number
 *    AND at least one street-name token beyond it (`2710 Tuckaseegee Rd` vs
 *    `2710 Tuckaseegee Road, Charlotte`). A shared house number alone is NOT
 *    corroboration — `123 Main St` vs `123 Unrelated Pkwy` is a disagreeing
 *    address and vetoes the match. A null/empty address leaves the name-shape
 *    signature authoritative.
 *
 * This leaves real number-named venues untouched: `300 East` (no street
 * token), `Studio 54` (no leading house number), `1900 Mexican Grill` (no
 * street token), `The Fillmore` (neither).
 *
 * **COR-86 — the disagreeing-house-number veto is deliberate, not
 * incidental.** When both strings carry a leading house number and the two
 * numbers differ, part 2 returns `false` even though the name is
 * unmistakably an address. The live example is
 * `301 McCullough Drive, University Executive Park Dr Ste 400, Charlotte, NC
 * 28262`, whose `address` column reads `101 North Tryon Street` — a different
 * building. The veto is kept, for three reasons:
 *
 *  - It is the same rule that spares `123 Main St` / `123 Unrelated Pkwy`.
 *    Carving out "…unless the name looks address-y enough" would make the
 *    corroboration step advisory, and part 2 exists precisely to be binding.
 *  - This detector drives a HARD FAIL (COR-25 `LOCATION_NAME_IS_ADDRESS`,
 *    which quarantines the event out of browse). A row whose two location
 *    fields contradict each other is a data-integrity problem for an operator
 *    to resolve, not a verdict this function should reach unilaterally.
 *  - Nothing is lost on the public surface: db-core's
 *    `unresolvedVenueExclusionSql` mirrors part 1 only, so the row is still
 *    hidden from `GET /api/v1/venues` while staying fully visible in admin.
 *
 * Pinned by test, so flipping the decision is a conscious edit.
 *
 * Pure + deterministic: no clock, no randomness, no catastrophic-backtracking
 * regex (both patterns are anchored, linear character classes).
 */
export function isAddressLikeVenueName(name: string, address: string | null): boolean {
  const trimmedName = name.trim();
  if (trimmedName.length === 0) return false;

  // Part 1 — name shape: leading house number + a street-type token.
  const houseNumberMatch = LEADING_HOUSE_NUMBER.exec(trimmedName);
  if (houseNumberMatch === null) return false;

  const tokens = trimmedName.toLowerCase().split(/[^a-z0-9#]+/);
  const hasStreetToken = tokens.some(
    (token) => STREET_TYPE_TOKENS.has(token) || /^#\d/.test(token),
  );
  if (!hasStreetToken) return false;

  // Part 2 — address corroboration (only when the address column has content).
  const trimmedAddress = typeof address === 'string' ? address.trim() : '';
  if (trimmedAddress.length === 0) return true;

  const normalizedName = normalizeForAddressComparison(trimmedName);
  const normalizedAddress = normalizeForAddressComparison(trimmedAddress);
  if (
    normalizedAddress.startsWith(normalizedName) ||
    normalizedName.startsWith(normalizedAddress)
  ) {
    return true;
  }

  // A shared leading house number alone is NOT corroboration — `123 Main St`
  // vs `123 Unrelated Pkwy` is a disagreeing address and must veto the match.
  // When the numbers match, additionally require street-content agreement: at
  // least one shared street-NAME token beyond the house number and generic
  // street-type/unit suffixes (`st`, `rd`, `ste`, …). This still flags
  // abbreviation drift (`… Rd` vs `… Road, Charlotte`) that the prefix check
  // above misses, without accepting number-only coincidences.
  const addressHouseNumberMatch = LEADING_HOUSE_NUMBER.exec(trimmedAddress);
  if (addressHouseNumberMatch === null || addressHouseNumberMatch[1] !== houseNumberMatch[1]) {
    return false;
  }

  const streetNameTokens = (value: string): ReadonlySet<string> =>
    new Set(
      value
        .split(' ')
        // Keep tokens that can identify a STREET: word-like tokens, plus
        // COR-86's ordinal street names (`4th`, `32nd`). The bare
        // "starts with a letter" rule dropped every ordinal along with the
        // house number and the zip, which left `150 W 32nd St` with nothing
        // but `w` to match on. Bare digits (house number, zip, unit) stay
        // excluded because the ordinal pattern is fully anchored. Generic
        // street-type suffixes identify no street and are always dropped.
        .filter(
          (token) =>
            (/^[a-z]/.test(token) || ORDINAL_STREET_NAME.test(token)) &&
            !STREET_TYPE_TOKENS.has(token),
        ),
    );

  const allNameTokens = streetNameTokens(normalizedName);
  // COR-86 — demote directions on the NAME side only when the name has some
  // other identity token to match on. See {@link DIRECTION_WORDS}: on
  // `1600 East Blvd` the direction IS the street name and dropping it would
  // empty the set, while on `150 W Main St` keeping it would let `west` alone
  // corroborate an address naming a different street. The ADDRESS side keeps
  // its directions either way — it only supplies the vocabulary the name's
  // identity tokens are matched against.
  const nameIdentityTokens = [...allNameTokens].filter((token) => !DIRECTION_WORDS.has(token));
  const nameTokens: ReadonlySet<string> =
    nameIdentityTokens.length > 0 ? new Set(nameIdentityTokens) : allNameTokens;
  const addressTokens = streetNameTokens(normalizedAddress);
  for (const token of nameTokens) {
    if (addressTokens.has(token)) return true;
  }
  return false;
}

function isValidEarthCoord(lat: number | null, lng: number | null): boolean {
  return (
    lat !== null &&
    lng !== null &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= LAT_MIN &&
    lat <= LAT_MAX &&
    lng >= LNG_MIN &&
    lng <= LNG_MAX &&
    // (0,0) "null island" is the classic geocode-failure sentinel.
    !(lat === 0 && lng === 0)
  );
}

function isWithinServiceArea(lat: number, lng: number): boolean {
  return (
    lat >= SERVICE_AREA.latMin &&
    lat <= SERVICE_AREA.latMax &&
    lng >= SERVICE_AREA.lngMin &&
    lng <= SERVICE_AREA.lngMax
  );
}

/** Clamp a raw dimension total to [0, 100]. */
function clampScore(value: number): number {
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}

function isTicketedSource(source: string | null): boolean {
  if (source === null) return false;
  const s = source.toLowerCase();
  return s === 'ticketmaster' || s === 'eventbrite' || s === 'official';
}

/**
 * PIP-167 (decision D6) — does this event tell ANY honest story about how a
 * user gets in?
 *
 * Sources are sellers and promoters, so at least one of these should always be
 * present. An event with none of them cannot be rendered without a CTA that
 * lies ("Get tickets" → to what price? is it even ticketed?), which is why the
 * caller may promote its absence to a quarantine.
 *
 * Evidence, in the order the sources actually publish it:
 *   1. A purchasable ticket link (`ticketUrl`) — you can buy in, price aside.
 *   2. A published price range (`priceMin` / `priceMax`, PIP-161) — either end
 *      counts; a source publishing only a floor ("from $25") is still a story.
 *      `0` is deliberately valid evidence: an explicit $0 IS a price story.
 *   3. An explicit free declaration (`isFree === true`). Strictly `true` —
 *      `null` means unknown and `false` means "confirmed paid", and neither is
 *      evidence of a price.
 *   4. An explicit RSVP signal (`rsvpOnly`) — no paid ticketing, but a real,
 *      honest entry story.
 *   5. `priceKnown` — the legacy pre-PIP-161 flag. Included so rows a
 *      transformer already vouched for are never quarantined merely because the
 *      newer columns are not backfilled yet. Conservative by design: this rule
 *      hard-fails, so it must under-fire rather than over-fire.
 *
 * Deliberately NOT evidence: `sourceUrl`. It is provenance (where the listing
 * came from), not proof you can buy a ticket — folding it in would make the
 * rule pass for essentially every row and mean nothing. This is exactly why
 * `ScorableEvent` keeps `ticketUrl` separate from the folded `sourceUrl`.
 *
 * Pure: reads only the event, no env, no clock.
 */
function hasPriceStory(event: ScorableEvent): boolean {
  if (trimmedOrEmpty(event.ticketUrl).length > 0) return true;
  if (event.priceMin !== null || event.priceMax !== null) return true;
  if (event.isFree === true) return true;
  if (event.rsvpOnly) return true;
  if (event.priceKnown) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Dimension scorers — each returns 0–100 attainment and pushes its reason
// codes. They never read the clock.
// ---------------------------------------------------------------------------

/**
 * Completeness (30): non-generic title, description ≥80 chars, image present,
 * category present, ticket URL for ticketed events.
 */
function scoreCompleteness(event: ScorableEvent, reasons: QualityReasonCode[]): number {
  let score = 0;

  // Title quality (25). Title presence/placeholder is hard-failed upstream;
  // here we reward a usefully-long real title.
  const title = trimmedOrEmpty(event.title);
  if (title.length >= MIN_USEFUL_TITLE_CHARS && !isPlaceholderTitle(title)) {
    score += 25;
  }

  // Identity copy (25). A genuine `description` still wins when it is long
  // enough and not synthetic. PIP-232: Ticketmaster no longer writes
  // `description` (that column was mislabelled policy); completeness then
  // concatenates `importantInfo` + `additionalInfo` so TM policy that used to
  // live in `description` keeps the same credit after the column split.
  // Short-only policy still earns nothing — same as a short description today.
  const identityCopy = resolveIdentityCopy(event);
  if (identityCopy.length >= MIN_DESCRIPTION_CHARS && !isSyntheticDescription(identityCopy)) {
    score += 25;
  } else {
    reasons.push('IDENTITY_DESCRIPTION_SHORT');
  }

  // Image (20).
  if (event.hasImage) {
    score += 20;
  } else {
    reasons.push('IDENTITY_IMAGE_MISSING');
  }

  // Category (20).
  if (event.categories.length > 0) {
    score += 20;
  } else {
    reasons.push('CLASSIFICATION_CATEGORY_MISSING');
  }

  // Ticket URL for ticketed events (10). For ticketed sources, a missing or
  // unusable source URL (the ticket link) is a commerce gap.
  //
  // PIP-135 — INTENTIONAL double-penalty, not a duplicate-read bug: the same
  // `event.sourceUrl` field is also penalized in `scoreSource` (20 pts →
  // `SOURCE_URL_MISSING`). Different lenses on one field — here it is
  // user-facing completeness (no purchasable ticket link); there it is
  // provenance/source-trust (no verifiable origin). For a ticketed source the
  // combined hit is −6.0 weighted points (10·0.30 here + 20·0.15 there), an
  // accepted, intended strength for a field core to a ticketing product.
  if (isTicketedSource(event.source)) {
    if (trimmedOrEmpty(event.sourceUrl).length > 0) {
      score += 10;
    } else {
      reasons.push('COMMERCE_TICKET_URL_INVALID');
    }
  } else {
    // Non-ticketed events are not penalized for lacking a ticket URL.
    score += 10;
  }

  return clampScore(score);
}

/**
 * Location integrity (25): valid geocode, coord sanity (Earth bounds + service
 * area), address specificity, venue↔city consistency. Returns the 0–100
 * subscore. The compound HARD_FAIL condition (invalid coords AND no usable
 * address) is re-checked by the scorer's hard-fail gate; here we only emit the
 * PENALTY/CAP reason codes.
 */
function scoreLocation(event: ScorableEvent, reasons: QualityReasonCode[]): number {
  let score = 0;

  const earthValid = isValidEarthCoord(event.latitude, event.longitude);

  // Geocode validity + coord sanity (40).
  if (event.latitude === null || event.longitude === null) {
    // No coordinate at all → missing geocode (CAP from premium downstream).
    reasons.push('LOCATION_GEOCODE_MISSING');
  } else if (!earthValid) {
    // Present but outside Earth bounds → invalid coordinates (PENALTY; the
    // scorer escalates to HARD_FAIL when there's also no usable address).
    reasons.push('LOCATION_COORDS_INVALID');
  } else if (!isWithinServiceArea(event.latitude, event.longitude)) {
    // On Earth but implausibly far from the service area → treat as invalid
    // (likely a swapped-axis / default geocode), penalize but never hard-fail.
    reasons.push('LOCATION_COORDS_INVALID');
    score += 10; // partial credit: a coordinate exists, just not plausible.
  } else {
    score += 40;
  }

  // Address specificity (35). A street-level address is best; city-only is a
  // placement CAP (premium not allowed).
  const address = trimmedOrEmpty(event.address);
  const city = trimmedOrEmpty(event.city);
  if (address.length > 0) {
    score += 35;
  } else if (city.length > 0) {
    score += 18;
    reasons.push('LOCATION_ADDRESS_CITY_ONLY');
  }
  // No address and no city contributes nothing here.

  // Venue↔city consistency (25). Conservative heuristic: if the venue name
  // explicitly embeds a DIFFERENT well-known city token than the recorded
  // city, that's a contradiction (e.g. "Atlanta Civic Center" recorded in
  // Charlotte). Otherwise (consistent, or nothing to compare) award the
  // points — we never punish a venue that simply doesn't repeat the city.
  // COR-25: an address-like venue "name" (e.g. `2710 Tuckaseegee Rd`) earns
  // NO consistency credit — it is not a venue name at all. Defensive: the
  // hard-fail gate quarantines these before dimension scoring runs, but keep
  // the reason + withheld credit here too for callers that bypass the gate.
  const venueName = trimmedOrEmpty(event.venueName);
  if (venueName.length > 0 && isAddressLikeVenueName(venueName, event.address)) {
    reasons.push('LOCATION_NAME_IS_ADDRESS');
  } else if (venueName.length > 0 && city.length > 0 && venueCityContradicts(venueName, city)) {
    reasons.push('LOCATION_VENUE_CITY_MISMATCH');
  } else {
    score += 25;
  }

  return clampScore(score);
}

/**
 * A small set of US city tokens we can confidently recognize inside a venue
 * name. Conservative on purpose — only well-known, unambiguous city words go
 * here so we don't false-positive on common substrings.
 */
const KNOWN_CITY_TOKENS: readonly string[] = [
  'charlotte',
  'atlanta',
  'raleigh',
  'durham',
  'greensboro',
  'asheville',
  'columbia',
  'nashville',
  'austin',
  'chicago',
  'boston',
  'denver',
  'seattle',
  'portland',
  'miami',
];

/**
 * Returns true iff the venue name explicitly names a *different* well-known
 * city than the recorded city. Only fires on an unambiguous contradiction.
 */
function venueCityContradicts(venueName: string, city: string): boolean {
  const venue = venueName.toLowerCase();
  const cityLower = city.toLowerCase();
  for (const token of KNOWN_CITY_TOKENS) {
    if (token !== cityLower && venue.includes(token)) {
      return true;
    }
  }
  return false;
}

/**
 * Temporal integrity (20): end>start coherence, plausible duration, timezone
 * present, startTimeKnown. INTEGRITY ONLY — never freshness, never "ended".
 * Returns the 0–100 subscore. The `endDate < date` impossible-ordering case is
 * hard-failed by the scorer's gate before this runs; start-missing likewise
 * (date === null).
 */
function scoreTemporal(event: ScorableEvent, reasons: QualityReasonCode[]): number {
  let score = 0;

  const start = event.date;
  const end = event.endDate;

  // End/start coherence + plausible duration (40 combined).
  if (start !== null && end !== null) {
    const durationMs = end.getTime() - start.getTime();
    if (durationMs < 0) {
      // Defensive: impossible ordering is hard-failed upstream, but keep the
      // reason here too for callers that bypass the gate.
      reasons.push('TEMPORAL_END_BEFORE_START');
    } else {
      score += 20; // coherent ordering
      const isLong = event.categories.some((c) => LONG_DURATION_CATEGORIES.has(c.toLowerCase()));
      const maxDuration = isLong ? MAX_DURATION_MS_LONG : MAX_DURATION_MS;
      if (durationMs >= MIN_DURATION_MS && durationMs <= maxDuration) {
        score += 20; // plausible duration
      } else {
        reasons.push('TEMPORAL_DURATION_IMPLAUSIBLE');
      }
    }
  } else if (start !== null) {
    // Start present, no end → coherent (single-instant) but duration unknown.
    // Award the coherence half; no duration penalty (absence isn't implausible).
    score += 20;
  }

  // Timezone present (30).
  if (trimmedOrEmpty(event.timezone).length > 0) {
    score += 30;
  } else {
    reasons.push('TEMPORAL_TIMEZONE_MISSING');
  }

  // startTimeKnown (30).
  if (event.startTimeKnown) {
    score += 30;
  } else {
    reasons.push('TEMPORAL_START_TIME_UNKNOWN');
  }

  return clampScore(score);
}

/**
 * Source trust (15): provenance tier (ported legacy reliability map),
 * sourceEventId present, source URL present, category confidence.
 */
function scoreSource(event: ScorableEvent, reasons: QualityReasonCode[]): number {
  let score = 0;

  // Provenance tier (50). Ported from legacy sourceReliability (0–1 → 0–50).
  const sourceKey = trimmedOrEmpty(event.source).toLowerCase();
  const trust =
    sourceKey.length > 0 ? (SOURCE_TRUST[sourceKey] ?? SOURCE_TRUST_UNKNOWN) : SOURCE_TRUST_UNKNOWN;
  score += trust * 50;
  if (trust < SOURCE_LOW_TRUST_THRESHOLD) {
    reasons.push('SOURCE_LOW_TRUST');
  }

  // sourceEventId present (20).
  if (trimmedOrEmpty(event.sourceEventId).length > 0) {
    score += 20;
  } else {
    reasons.push('SOURCE_ID_MISSING');
  }

  // source URL present (20).
  //
  // PIP-135 — INTENTIONAL double-penalty, not a duplicate-read bug: the same
  // `event.sourceUrl` field is also penalized in `scoreCompleteness` (10 pts →
  // `COMMERCE_TICKET_URL_INVALID`) when the source is ticketed. Different
  // lenses on one field — there it is user-facing completeness (no purchasable
  // ticket link); here it is provenance/source-trust (no verifiable origin).
  // For a ticketed source the combined hit is −6.0 weighted points
  // (10·0.30 there + 20·0.15 here), an accepted, intended strength.
  if (trimmedOrEmpty(event.sourceUrl).length > 0) {
    score += 20;
  } else {
    reasons.push('SOURCE_URL_MISSING');
  }

  // Category confidence (10): a classified event has higher source confidence.
  if (event.categories.length > 0) {
    score += 10;
  } else {
    reasons.push('SOURCE_CATEGORY_LOW_CONFIDENCE');
  }

  return clampScore(score);
}

/**
 * Readiness (10): priceKnown, attributed artist bio (PIP-146), data richness
 * (images / categories acting as tags).
 *
 * Sub-signal weights (sum 100): priceKnown 30, images 25, categories 25,
 * artist bio 20. The bio signal is tri-state ({@link ScorableEvent.hasArtistBio}):
 * `undefined` means the event has no lineup (or the caller didn't include the
 * join) — the signal is N/A and earns full neutral credit, mirroring how
 * non-ticketed events are not penalized for lacking a ticket URL. Only an
 * event that HAS linked artists with no attributed bio loses the points (and
 * emits `READINESS_ARTIST_BIO_MISSING`).
 */
function scoreReadiness(event: ScorableEvent, reasons: QualityReasonCode[]): number {
  let score = 0;

  // priceKnown (30).
  if (event.priceKnown) {
    score += 30;
  }

  // Data richness: multiple images (25).
  if (event.imageUrls.length >= 2) {
    score += 25;
  } else if (event.imageUrls.length === 1) {
    score += 12;
  }

  // Data richness: categories acting as tags (25).
  if (event.categories.length >= 2) {
    score += 25;
  } else if (event.categories.length === 1) {
    score += 12;
  }

  // Attributed artist bio (20) — PIP-146. Neutral when N/A (no lineup).
  if (event.hasArtistBio === false) {
    reasons.push('READINESS_ARTIST_BIO_MISSING');
  } else {
    score += 20;
  }

  return clampScore(score);
}

// ---------------------------------------------------------------------------
// Banding + caps
// ---------------------------------------------------------------------------

/** Maps a structural score to its raw band, before caps/quarantine. */
function bandForScore(score: number): QualityStatus {
  if (score >= BAND_PREMIUM_MIN) return 'PREMIUM';
  if (score >= BAND_STANDARD_MIN) return 'STANDARD';
  if (score >= BAND_LOW_CONFIDENCE_MIN) return 'LOW_CONFIDENCE';
  return 'QUARANTINED';
}

/**
 * Reason codes that, when present, forbid PREMIUM placement (cap → STANDARD).
 * These are "critical defects" per COR-18: city-only address, low-trust source,
 * missing geocode.
 */
const PREMIUM_BLOCKING_DEFECTS: ReadonlySet<QualityReasonCode> = new Set<QualityReasonCode>([
  'LOCATION_ADDRESS_CITY_ONLY',
  'SOURCE_LOW_TRUST',
  'LOCATION_GEOCODE_MISSING',
]);

// Factory (not a shared const): the hard-fail path returns a fresh zeroed
// subscores object each call, so a consumer mutating `result.subscores` can
// never poison a later result. Preserves the purity/determinism contract.
function zeroSubscores(): QualitySubscores {
  return {
    completeness: 0,
    location: 0,
    temporal: 0,
    source: 0,
    readiness: 0,
  };
}

// ---------------------------------------------------------------------------
// Public scorer
// ---------------------------------------------------------------------------

/**
 * Score an event for placement. Pure + deterministic.
 *
 * @param input    Flat scorer-input view of a persisted event.
 * @param context  Optional caller-supplied review/dedup/safety signals that are
 *                 NOT derivable from the event alone. Pure: same context →
 *                 same result.
 */
export function scoreEventForPlacement(
  input: ScorableEvent,
  context: ScorePlacementContext = {},
): PlacementScore {
  // -----------------------------------------------------------------------
  // 1. Hard-fail gate. Any of these → immediate QUARANTINE, score 0, with the
  //    set of reasons that fired (we collect ALL applicable hard-fail codes).
  // -----------------------------------------------------------------------
  const hardFailReasons: QualityReasonCode[] = [];

  const title = trimmedOrEmpty(input.title);
  if (title.length === 0) {
    hardFailReasons.push('IDENTITY_TITLE_MISSING');
  } else if (isPlaceholderTitle(title)) {
    hardFailReasons.push('IDENTITY_TITLE_PLACEHOLDER');
  }

  if (input.date === null) {
    hardFailReasons.push('TEMPORAL_START_MISSING');
  }

  const venueName = trimmedOrEmpty(input.venueName);
  if (venueName.length === 0) {
    hardFailReasons.push('LOCATION_NAME_MISSING');
  } else if (isAddressLikeVenueName(venueName, input.address)) {
    // COR-25 — a non-empty venue "name" that is actually a raw street address
    // (e.g. `2710 Tuckaseegee Rd`) is as useless as a missing one, and worse:
    // without this gate it even earned the venue↔city consistency credit.
    hardFailReasons.push('LOCATION_NAME_IS_ADDRESS');
  }

  // endDate < date is impossible ordering.
  if (
    input.date !== null &&
    input.endDate !== null &&
    input.endDate.getTime() < input.date.getTime()
  ) {
    hardFailReasons.push('TEMPORAL_END_BEFORE_START');
  }

  // Invalid coordinates AND no usable address.
  const hasUsableAddress =
    trimmedOrEmpty(input.address).length > 0 || trimmedOrEmpty(input.city).length > 0;
  const hasCoord = input.latitude !== null && input.longitude !== null;
  if (hasCoord && !isValidEarthCoord(input.latitude, input.longitude) && !hasUsableAddress) {
    hardFailReasons.push('LOCATION_COORDS_INVALID');
  }

  if (context.safetyFlag === true) {
    hardFailReasons.push('POLICY_SAFETY_FLAG');
  }

  // PIP-167 (D6) — zero price evidence. OPT-IN: only evaluated when the caller
  // passes `priceStoryRuleEnabled: true` (resolved from `LOCI_PRICE_STORY_RULE`,
  // default OFF). While the rule is off this branch is skipped entirely, so the
  // reason never appears and scoring is byte-identical to pre-PIP-167 — the
  // rollout-safety property the AC pins.
  if (context.priceStoryRuleEnabled === true && !hasPriceStory(input)) {
    hardFailReasons.push('PRICE_STORY_MISSING');
  }

  // COR-115 — the persisted link verdict is dead. Unlike PIP-167's rule this is
  // not opt-in: a `dead` verdict is the same proof the sweeper / dead-listing
  // path quarantine on, so there is no coverage gap to wait out. The scorer has
  // no lifecycle status, so it also holds rows PIP-255 deliberately left
  // un-quarantined (already out of browse) once something re-scores them —
  // invisible to users either way. An omitted `linkStatus` (a path that did not
  // project the column) under-enforces. Exact match against the shared constant
  // backend-api's `resolveCtaHref` also uses, so the CTA and the quarantine
  // cannot disagree about what "dead" means; `suspect` and `ok` never fire.
  if (input.linkStatus === DEAD_LINK_STATUS) {
    hardFailReasons.push('SOURCE_LINK_DEAD');
  }

  if (hardFailReasons.length > 0) {
    return {
      score: QUARANTINE_SCORE,
      status: 'QUARANTINED',
      reasons: hardFailReasons,
      subscores: zeroSubscores(),
    };
  }

  // -----------------------------------------------------------------------
  // 2. Dimension scoring. Reasons accumulate as deductions fire.
  // -----------------------------------------------------------------------
  const reasons: QualityReasonCode[] = [];

  const completeness = scoreCompleteness(input, reasons);
  const location = scoreLocation(input, reasons);
  const temporal = scoreTemporal(input, reasons);
  const source = scoreSource(input, reasons);
  const readiness = scoreReadiness(input, reasons);

  const subscores: QualitySubscores = {
    completeness,
    location,
    temporal,
    source,
    readiness,
  };

  const structuralScore = Math.round(
    completeness * WEIGHTS.completeness +
      location * WEIGHTS.location +
      temporal * WEIGHTS.temporal +
      source * WEIGHTS.source +
      readiness * WEIGHTS.readiness,
  );

  // -----------------------------------------------------------------------
  // 3. Caps. Apply caller/context caps and critical-defect caps, lowering the
  //    effective score ceiling. The score itself is clamped to the cap so the
  //    final band reflects the cap.
  // -----------------------------------------------------------------------
  let cappedScore = structuralScore;

  // Review flag / fuzzy-duplicate → cap at LOW_CONFIDENCE (74).
  if (context.reviewFlagUnresolved === true) {
    reasons.push('REVIEW_FLAG_UNRESOLVED');
    cappedScore = Math.min(cappedScore, CAP_LOW_CONFIDENCE_MAX);
  }
  if (context.fuzzyDuplicatePendingMerge === true) {
    reasons.push('DUPLICATE_FUZZY_PENDING_MERGE');
    cappedScore = Math.min(cappedScore, CAP_LOW_CONFIDENCE_MAX);
  }

  // PIP-407 — a standing commerce product is not a confident event placement,
  // however complete its fields. Both cap at LOW_CONFIDENCE (74), which only
  // hides anything once an operator turns the quality gate on; the public
  // series collapse works independently of the gate.
  if (standingProductTitleNoun(title) !== null) {
    reasons.push('IDENTITY_TITLE_NON_EVENT');
    cappedScore = Math.min(cappedScore, CAP_LOW_CONFIDENCE_MAX);
  }
  // The sweep's persisted verdict (`Event.seriesOccurrences`, non-null ⇔
  // member). The scorer reads it; detection needs the sibling rows and lives
  // in the data-pipeline sweep. Only a series from an open, self-serve lane
  // (`SERIES_EVERGREEN_SOURCES`: Eventbrite, venue-site) is capped — a
  // Ticketmaster run of real performances collapses but keeps its band.
  if (
    input.seriesOccurrences != null &&
    input.seriesOccurrences >= 1 &&
    isSeriesEvergreenSource(input.source)
  ) {
    reasons.push('SERIES_EVERGREEN');
    cappedScore = Math.min(cappedScore, CAP_LOW_CONFIDENCE_MAX);
  }

  // Critical placement defects → cap at STANDARD (89): premium not allowed.
  const hasPremiumBlocker = reasons.some((code) => PREMIUM_BLOCKING_DEFECTS.has(code));
  if (hasPremiumBlocker) {
    cappedScore = Math.min(cappedScore, CAP_STANDARD_MAX);
  }

  // -----------------------------------------------------------------------
  // 4. Band the capped score. Quarantine (<60) overrides everything.
  // -----------------------------------------------------------------------
  const status = bandForScore(cappedScore);

  return {
    score: cappedScore,
    status,
    reasons,
    subscores,
  };
}
